import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const lineReveal = {
  hidden: { y: "112%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.14 },
  }),
};

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative overflow-hidden pt-40 md:pt-52 pb-24 md:pb-36"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16 lg:gap-12 items-center">
        <div className="lg:col-span-6">
          <h1
            className="font-heading font-bold text-[#1F2043] tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-[3.8rem]"
            data-testid="hero-headline"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} variants={lineReveal} initial="hidden" animate="visible" className="block">
                Building inner readiness
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span custom={1} variants={lineReveal} initial="hidden" animate="visible" className="block text-[#00867A]">
                in India&rsquo;s care leavers.
              </motion.span>
            </span>
          </h1>
          <motion.p
            {...fadeUp(0.75)}
            className="font-body mt-7 text-base md:text-lg font-light leading-relaxed text-[#3C3C3B] max-w-xl"
            data-testid="hero-subline"
          >
            We work with adolescents in Child Care Institutions and young adults transitioning out
            of them across Maharashtra.
          </motion.p>
          <motion.div {...fadeUp(0.95)} className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#contact" data-testid="hero-cta-partner" className="btn-primary">
              Partner with us
              <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#our-work" data-testid="hero-cta-see-work" className="btn-outline">
              See our work
            </a>
          </motion.div>
        </div>
        <div className="lg:col-span-6 relative">
          <div
            className="absolute -top-6 -right-4 w-24 h-24 bg-[#BCCF00]/55 blob-organic"
            aria-hidden="true"
          />
          <div
            className="absolute -bottom-5 -left-5 w-16 h-16 bg-[#FFED00]/75 blob-organic"
            aria-hidden="true"
          />
          <motion.div style={{ y: imgY }} className="relative">
            <img
              src="/assets/hero-illustration.png"
              alt="Illustration of Indian adolescents and young adults sitting in a circle during a Sachet Foundation program session"
              data-testid="hero-image"
              className="relative w-full rounded-[2.5rem] object-cover aspect-[4/3] shadow-[0_24px_70px_rgb(31,32,67,0.12)]"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 1.15 }}
            className="absolute -bottom-7 left-5 md:-left-10 bg-white rounded-2xl shadow-[0_16px_50px_rgb(31,32,67,0.12)] border border-[#1F2043]/5 px-6 py-4"
            data-testid="hero-stat-chip"
          >
            <p className="font-heading text-2xl font-bold text-[#00867A] leading-none">100+</p>
            <p className="font-body text-sm font-light text-[#3C3C3B] mt-1.5">
              students reached through our programs
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
